import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useForm, Controller } from "react-hook-form";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import NumberInput from "@/app/components/ui/Inputs/NumberInput";
import useFormValidations from "@/app/hooks/useFormValidations";
import { CiShare1 } from "react-icons/ci";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import {
  ICreateDebitNoteProps,
  usePaymentUserCreateDebitNoteAPI,
} from "@/app/hooks/api_hooks/payment/usePaymentUserCreateDebitNoteAPI";
import { usePaymentCreditNoteDetailsAPI } from "@/app/hooks/api_hooks/payment/usePaymentCreditNoteDetailsAPI";
import { usePaymentCreditNoteListAPI } from "@/app/hooks/api_hooks/payment/usePaymentCreditNoteListAPI";
import { usePaymentUserWalletAPI } from "@/app/hooks/api_hooks/payment/usePaymentUserWalletAPI";

export interface IIcreditNote {
  amount: number;
  comment: string;
}

const CreditNoteCretionModal = ({ open, setOpen, creditNoteId }) => {
  const cancelButtonRef = useRef(null);
  const { getRouteKey } = useRouter();
  const { customerId } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id")).routeState.state as ICustomerProfileState;
  const { forOnlyNumbersWithDecimal } = useFormValidations();

  const mappedClinicId = useAppSelector((state) => state?.root?.UIStates?.mappedClinicId);
  const { mappedClinicName } = useAppSelector((state) => state.root.UIStates);

  const { execute: createDebitNote } = usePaymentUserCreateDebitNoteAPI();
  const { execute: fetchCreditNote } = usePaymentCreditNoteListAPI();
  const { execute: fetchUserWalletDetails } = usePaymentUserWalletAPI();
  const { execute: fetchCreditNoteDetails, creditNoteDetails } = usePaymentCreditNoteDetailsAPI();

  const formHook = useForm<IIcreditNote>({
    mode: "onChange",
    defaultValues: {},
  });

  const onSubmit = (data) => {
    const constructedBody: ICreateDebitNoteProps = {
      clinic_id: mappedClinicId,
      clinic_name: mappedClinicName,
      comments: data.comment,
      debit_amount: parseFloat(data.amount),
      credit_note_receipt_id: creditNoteId,
      user_id: customerId,
    };

    createDebitNote(constructedBody).then(({ status }) => {
      if (status) {
        fetchUserWalletDetails(customerId);
        fetchCreditNote(customerId, 1, 10);
        formHook.reset();
        setOpen(false);
      }
    });
  };

  useEffect(() => {
    if (creditNoteId) {
      fetchCreditNoteDetails(creditNoteId);
    }
  }, [creditNoteId]);

  return (
    <div>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={() => {
            setOpen(false);
            formHook.reset();
          }}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
            </Transition.Child>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <form
                onSubmit={formHook.handleSubmit(onSubmit)}
                className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:max-w-4xl"
              >
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex justify-between items-center mt-0 border-b pb-1">
                    <h2 className="text-sm font-medium">Raise Debit Note Request</h2>

                    <span
                      onClick={() => {
                        formHook.reset();
                        setOpen(false);
                      }}
                    >
                      <XMarkIcon className="h-4 w-4 cursor-pointer" />
                    </span>
                  </div>
                  <div className="flex items-center pt-5 ">
                    <div className=" items-start flex  w-3/12">
                      <p>Credit Note ID</p>
                    </div>
                    <p>{creditNoteDetails?.credit_note_receipt_id}</p>
                  </div>
                  <div className="flex items-center pt-5">
                    <div className=" items-start flex w-3/12 ">
                      <p>Amount Credited</p>
                    </div>
                    <div className="flex flex-col   ">
                      <p>{creditNoteDetails?.credit_amount}</p>
                    </div>
                  </div>

                  {creditNoteDetails?.total_debit_amount !== 0 && (
                    <div className="flex items-center pt-5 ">
                      <div className=" items-start flex w-3/12">
                        <p>Already Debited</p>
                      </div>
                      <div className="flex flex-col   ">
                        <p>{creditNoteDetails?.total_debit_amount}</p>
                      </div>
                    </div>
                  )}
                  {creditNoteDetails?.debit_note_receipt_ids?.length > 0 && (
                    <div className="flex items-center pt-5 ">
                      <div className=" items-start flex w-3/12">
                        <p>Previous Debit Notes</p>
                      </div>
                      <div className="flex gap-1  items-center ">
                        {creditNoteDetails?.debit_note_receipt_ids?.map((item: any) => {
                          return (
                            <p className="flex flex-row gap-2 text-[#0068FF] font-medium  items-center ">{item},</p>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center pt-5 ">
                    <div className=" items-start flex w-3/12">
                      <p>Debit Amount</p>
                    </div>
                    <div className="  w-1/3 ">
                      <NumberInput
                        className="text-xs "
                        placeholder="Enter Amount"
                        register={formHook.register(`amount`, {
                          required: true,
                          ...forOnlyNumbersWithDecimal.validations,
                          validate: (value: any) => {
                            // Trim leading zeros
                            const trimmedValue = value.replace(/^0+/, "") || "0"; // Remove all leading zeros, default to '0' if empty
                            // Assign trimmed value to form field
                            formHook.setValue(`amount`, trimmedValue);

                            // Validate the amount is less than or equal to `dt`

                            if (
                              parseFloat(trimmedValue) >
                              creditNoteDetails?.credit_amount - creditNoteDetails?.total_debit_amount
                            ) {
                              return `Amount cannot exceed`;
                            }
                            return null;
                          },
                        })}
                        fieldError={formHook.formState.errors.amount}
                        errorMessages={[{ message: "", type: "required" }, forOnlyNumbersWithDecimal.errors]}
                      />
                      <p className="text-red-400 text-[9px] absolute ">
                        {formHook.getValues("amount") >
                          creditNoteDetails?.credit_amount - creditNoteDetails?.total_debit_amount &&
                          "Amount cannot be more than total"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center pt-5">
                    <div className=" items-start flex w-3/12">
                      <p>Comment</p>
                    </div>
                    <div className="  ">
                      <Controller
                        name="comment"
                        control={formHook.control} // Ensure you're using the correct form instance
                        rules={{
                          required: "Please provide a reason.",
                          validate: (value) => value.trim() !== "" || "Reason cannot be blank.",
                        }}
                        render={({ field }) => (
                          <textarea
                            {...field}
                            rows={4}
                            cols={70}
                            placeholder="Add comment...."
                            className={`w-full p-2 border ${formHook.formState.errors.comment ? "border-red-400" : "border-gray-300"} rounded-md text-sm`}
                          />
                        )}
                      />
                      {formHook?.formState?.errors?.comment && (
                        <p className="text-red-500 absolute text-[10px] -mt-1">Required</p>
                      )}
                    </div>
                  </div>
                  <div className="py-4">
                    <hr />
                  </div>
                  <div className="flex justify-end gap-4 my-2">
                    <button
                      type="button"
                      className="rounded-lg bg-[#72767f1a] px-4 py-2 text-sm text-[#000000] font-medium "
                      onClick={() => {
                        formHook.reset();
                        setOpen(false);
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="bg-[var(--color-primary)] px-4 sm:px-6 py-1.5 sm:py-2 rounded-lg text-sm text-white ">
                      Raise Request
                    </button>
                  </div>
                </div>
              </form>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default CreditNoteCretionModal;
